// ===== TEORÍA MUSICAL - ESCALAS Y MODOS =====

export type ScaleType = "major" | "minor_natural" | "minor_harmonic" | "minor_melodic" | "chromatic"
export type ModeType = "ionian" | "dorian" | "phrygian" | "lydian" | "mixolydian" | "aeolian" | "locrian"

export interface ScaleSettings {
   scale: ScaleType
   mode: ModeType
}

export interface TempoSettings {
   bpm: number
}

export interface VolumeSettings {
   master: number
   melody: number
   accompaniment: number
   bass: number
}

// Intervalos de semitonos para cada escala (en relación a la tónica)
export const SCALE_INTERVALS: Record<ScaleType, number[]> = {
   major: [0, 2, 4, 5, 7, 9, 11],           // Mayor: W-W-H-W-W-W-H
   minor_natural: [0, 2, 3, 5, 7, 8, 10],   // Menor natural: W-H-W-W-H-W-W
   minor_harmonic: [0, 2, 3, 5, 7, 8, 11],  // Menor armónica: W-H-W-W-H-WH-H
   minor_melodic: [0, 2, 3, 5, 7, 9, 11],   // Menor melódica (ascendente): W-H-W-W-W-W-H
   chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], // Cromática: todos los semitonos
}

// Intervalos para cada modo (en relación a la tónica)
export const MODE_INTERVALS: Record<ModeType, number[]> = {
   ionian: [0, 2, 4, 5, 7, 9, 11],      // Jónico = Mayor
   dorian: [0, 2, 3, 5, 7, 9, 10],      // Dórico: Mayor con 3ª y 7ª menores
   phrygian: [0, 1, 3, 5, 7, 8, 10],    // Frigio: Menor con 2ª menor
   lydian: [0, 2, 4, 6, 7, 9, 11],      // Lidio: Mayor con 4ª aumentada
   mixolydian: [0, 2, 4, 5, 7, 9, 10],  // Mixolidio: Mayor con 7ª menor
   aeolian: [0, 2, 3, 5, 7, 8, 10],     // Eólico = Menor natural
   locrian: [0, 1, 3, 5, 6, 8, 10],     // Locrio: Menor con 2ª y 5ª disminuidas
}

// Nombres legibles
export const SCALE_NAMES: Record<ScaleType, string> = {
   major: "Mayor",
   minor_natural: "Menor Natural",
   minor_harmonic: "Menor Armónica",
   minor_melodic: "Menor Melódica",
   chromatic: "Cromática",
}

export const MODE_NAMES: Record<ModeType, string> = {
   ionian: "Jónico (Mayor)",
   dorian: "Dórico",
   phrygian: "Frigio",
   lydian: "Lidio",
   mixolydian: "Mixolidio",
   aeolian: "Eólico (Menor)",
   locrian: "Locrio",
}

// Obtiene los intervalos activos según escala y modo
export function getActiveIntervals(scale: ScaleType, mode: ModeType): number[] {
   // Si es cromática, devuelve todos los intervalos
   if (scale === "chromatic") {
      return SCALE_INTERVALS.chromatic
   }
   
   // Si el modo es diferente de jónico, usar los intervalos del modo
   if (mode !== "ionian") {
      return MODE_INTERVALS[mode]
   }
   
   // Si es jónico, usar los intervalos de la escala seleccionada
   return SCALE_INTERVALS[scale]
}

// Genera una escala completa a partir de una nota raíz MIDI
export function generateScale(rootNote: number, intervals: number[]): number[] {
   const scale: number[] = []
   const octaveRange = 3 // Generar 3 octavas
   
   for (let octave = 0; octave < octaveRange; octave++) {
      for (const interval of intervals) {
         scale.push(rootNote + interval + (octave * 12))
      }
   }
   
   return scale
}

// Convierte un grado de la escala a nota MIDI
export function degreeToMidi(degree: number, rootNote: number, intervals: number[]): number {
   const octave = Math.floor(degree / intervals.length)
   const index = degree % intervals.length
   return rootNote + intervals[index] + (octave * 12)
}

// Mapeo de notas a nombres
export const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"]

export function midiToNoteName(midi: number): string {
   const octave = Math.floor(midi / 12) - 1
   const noteIndex = midi % 12
   return `${NOTE_NAMES[noteIndex]}${octave}`
}

// Tonalidades comunes (root notes en MIDI)
export const COMMON_KEYS = [
   { name: "C", midi: 60 },
   { name: "G", midi: 67 },
   { name: "D", midi: 62 },
   { name: "A", midi: 69 },
   { name: "E", midi: 64 },
   { name: "F", midi: 65 },
   { name: "B♭", midi: 70 },
   { name: "E♭", midi: 63 },
   { name: "A♭", midi: 68 },
   { name: "D♭", midi: 61 },
]
